import api from './api';
import { translateError } from '../utils/errorTranslations';
import { 
  setToStorage, 
  removeFromStorage, 
  getFromStorage 
} from '../utils/storage';
import { 
  TOKEN_STORAGE_KEY, 
  REFRESH_TOKEN_STORAGE_KEY,
  AUTH_USER_STORAGE_KEY
} from '../utils/constants';

/**
 * Decodifica um JWT sem verificar a assinatura para extrair o payload
 * @param {string} token - Token JWT
 * @returns {object|null} Payload do token ou null se inválido
 */
const decodeJWT = (token) => {
  try {
    if (!token) return null;
    
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.warn('Failed to decode JWT:', error);
    return null;
  }
};

/**
 * Verifica se um token JWT está expirado
 * @param {string} token - Token JWT
 * @returns {boolean} True se expirado, false caso contrário
 */
const isTokenExpired = (token) => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return true;
  
  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now;
};

/**
 * Remove tokens expirados do localStorage
 * @returns {boolean} True se algum token foi removido
 */
export const cleanupExpiredTokens = () => {
  let cleaned = false;
  
  // Verificar access token
  const accessToken = getFromStorage(TOKEN_STORAGE_KEY, false);
  if (accessToken && isTokenExpired(accessToken)) {
    console.log('🧹 [CLEANUP] Removendo access token expirado do localStorage');
    console.log('🧹 [CLEANUP] Access token expirou:', decodeJWT(accessToken)?.exp ? new Date(decodeJWT(accessToken).exp * 1000).toLocaleString() : 'sem expiração');
    removeFromStorage(TOKEN_STORAGE_KEY);
    cleaned = true;
  }
  
  // Verificar refresh token
  const refreshToken = getFromStorage(REFRESH_TOKEN_STORAGE_KEY, false);
  if (refreshToken && isTokenExpired(refreshToken)) {
    console.log('🧹 [CLEANUP] Removendo refresh token expirado do localStorage');
    console.log('🧹 [CLEANUP] Refresh token expirou:', decodeJWT(refreshToken)?.exp ? new Date(decodeJWT(refreshToken).exp * 1000).toLocaleString() : 'sem expiração');
    removeFromStorage(REFRESH_TOKEN_STORAGE_KEY);
    removeFromStorage(AUTH_USER_STORAGE_KEY); // Remover dados do usuário também
    cleaned = true;
    
    // Disparar evento de logout se refresh token expirou
    window.dispatchEvent(new CustomEvent('auth:logout'));
    console.log('🧹 [CLEANUP] Evento de logout disparado devido a refresh token expirado');
  }
  
  if (cleaned) {
    console.log('🧹 [CLEANUP] Limpeza de tokens executada com sucesso');
  }
  
  return cleaned;
};

// ✅ SINGLETON: Variável global para controlar único interval de limpeza
let globalCleanupInterval = null;
let isCleanupActive = false;

/**
 * Inicia o processo de limpeza automática de tokens
 * Verifica a cada 5 minutos se há tokens expirados
 * ✅ SINGLETON: Protege contra múltiplos intervals
 */
export const startTokenCleanup = () => {
  // ✅ PROTEÇÃO: Se já existe sistema ativo, não criar outro
  if (isCleanupActive && globalCleanupInterval) {
    console.log('🧹 [CLEANUP] Sistema já está ativo - reutilizando instância existente');
    return () => {
      console.log('🧹 [CLEANUP] Tentativa de parada ignorada - sistema compartilhado');
    };
  }
  
  console.log('🧹 [CLEANUP] Iniciando sistema de limpeza automática de tokens (SINGLETON)');
  isCleanupActive = true;
  
  // Limpeza inicial
  const initialCleanup = cleanupExpiredTokens();
  if (initialCleanup) {
    console.log('🧹 [CLEANUP] Limpeza inicial executada - tokens expirados removidos');
  } else {
    console.log('🧹 [CLEANUP] Limpeza inicial executada - nenhum token expirado encontrado');
  }
  
  // ✅ SINGLETON: Configurar limpeza periódica única
  globalCleanupInterval = setInterval(() => {
    console.log('🧹 [CLEANUP] Executando verificação periódica de tokens expirados');
    const cleaned = cleanupExpiredTokens();
    if (cleaned) {
      console.log('🧹 [CLEANUP] Limpeza automática executada - tokens expirados removidos');
    } else {
      console.log('🧹 [CLEANUP] Verificação periódica completa - nenhum token expirado');
    }
  }, 5 * 60 * 1000); // 5 minutos
  
  console.log('🧹 [CLEANUP] Sistema SINGLETON configurado para verificação a cada 5 minutos');
  
  // ✅ SINGLETON: Retornar função de cleanup que para o sistema global
  return () => {
    console.log('🧹 [CLEANUP] Parando sistema de limpeza automática de tokens (SINGLETON)');
    
    if (globalCleanupInterval) {
      clearInterval(globalCleanupInterval);
      globalCleanupInterval = null;
    }
    
    isCleanupActive = false;
    console.log('🧹 [CLEANUP] Sistema SINGLETON parado com sucesso');
  };
};

/**
 * Verifica se o usuário está autenticado com tokens válidos
 * @returns {boolean} True se autenticado com tokens válidos
 */
export const isAuthenticated = () => {
  // Primeiro limpar tokens expirados
  cleanupExpiredTokens();
  
  const accessToken = getFromStorage(TOKEN_STORAGE_KEY, false);
  const refreshToken = getFromStorage(REFRESH_TOKEN_STORAGE_KEY, false);
  
  // Verificar se pelo menos o refresh token está presente e válido
  return refreshToken && !isTokenExpired(refreshToken);
};

/**
 * Realiza login do usuário
 * @param {string} username - Nome de usuário
 * @param {string} password - Senha
 * @returns {Promise<object>} Dados do usuário e tokens
 */
export const loginUser = async (username, password) => {
  try {
    console.log('🔐 [AUTH_SERVICE] Iniciando login para:', username);
    const { data } = await api.post('/auth/token/', { username, password });
    
    console.log('🔐 [AUTH_SERVICE] Login bem-sucedido para:', username);
    
    // Armazenar tokens
    setToStorage(TOKEN_STORAGE_KEY, data.access_token);
    setToStorage(REFRESH_TOKEN_STORAGE_KEY, data.refresh_token);
    
    // Armazenar dados básicos do usuário
    setToStorage(AUTH_USER_STORAGE_KEY, { 
      username,
      is_admin: data.is_admin,
      full_name: data.full_name
    });
    
    return data;
  } catch (error) {
    console.error('🔐 [AUTH_SERVICE] Erro durante login:', error);
    
    // Handle specific error cases
    if (error.response) {
      const status = error.response.status;
      const detail = error.response.data?.detail || error.response.data?.message;
      
      console.error('🔐 [AUTH_SERVICE] Status do erro:', status, 'Detalhe:', detail);
      
      switch (status) {
        case 401:
          console.error('🔐 [AUTH_SERVICE] Erro 401 - Credenciais inválidas');
          throw new Error('Invalid username or password');
        case 400:
          console.error('🔐 [AUTH_SERVICE] Erro 400 - Requisição inválida');
          throw new Error('Invalid login request');
        case 403:
          console.error('🔐 [AUTH_SERVICE] Erro 403 - Acesso negado');
          throw new Error('Access denied');
        case 404:
          console.error('🔐 [AUTH_SERVICE] Erro 404 - Usuário não encontrado');
          throw new Error('User not found');
        case 429:
          console.error('🔐 [AUTH_SERVICE] Erro 429 - Muitas tentativas');
          throw new Error('Too many login attempts');
        case 500:
          console.error('🔐 [AUTH_SERVICE] Erro 500 - Erro do servidor');
          throw new Error('Server error');
        default:
          console.error('🔐 [AUTH_SERVICE] Erro desconhecido:', status);
          throw new Error(detail || 'Login failed');
      }
    } else if (error.request) {
      console.error('🔐 [AUTH_SERVICE] Erro de conexão de rede');
      throw new Error('Network connection error');
    } else {
      console.error('🔐 [AUTH_SERVICE] Erro desconhecido:', error.message);
      throw new Error('Login failed');
    }
  }
};

/**
 * Renova o token de acesso usando o refresh token
 * 
 * ⚠️  IMPORTANTE: Esta função pode ser chamada diretamente, mas o sistema 
 * automático de refresh (api.js) já possui proteção contra concorrência.
 * Use esta função apenas para refresh manual/explícito.
 * 
 * @returns {Promise<object>} Novos tokens
 */
export const refreshToken = async () => {
  try {
    console.log('🔄 [AUTH_SERVICE] Refresh manual iniciado');
    const refreshTokenValue = getFromStorage(REFRESH_TOKEN_STORAGE_KEY, false);
    if (!refreshTokenValue) {
      throw new Error('Refresh token not available');
    }
    
    const { data } = await api.post('/auth/token/refresh/', {
      refresh_token: refreshTokenValue
    });
    
    // Atualizar token de acesso no armazenamento
    setToStorage(TOKEN_STORAGE_KEY, data.access_token);
    
    // Se o backend retornar um novo refresh token, atualizá-lo também
    if (data.refresh_token) {
      setToStorage(REFRESH_TOKEN_STORAGE_KEY, data.refresh_token);
    }
    
    console.log('🔄 [AUTH_SERVICE] Refresh manual completado com sucesso');
    return data;
  } catch (error) {
    console.error('🔄 [AUTH_SERVICE] Error refreshing token:', error);
    
    // ✅ DETECÇÃO: Log específico para erro de blacklist
    if (error.response?.data?.detail?.includes('blacklisted')) {
      console.warn('🔄 [AUTH_SERVICE] Token blacklisted detectado - possível concorrência');
    }
    
    throw new Error(error.response?.data?.detail || 'Error refreshing token');
  }
};

/**
 * Realiza o logout do usuário
 * @returns {Promise<boolean>} Se o logout foi bem-sucedido
 */
export const logoutUser = async () => {
  try {
    const refreshTokenValue = getFromStorage(REFRESH_TOKEN_STORAGE_KEY, false);
    
    if (refreshTokenValue) {
      // Revogar token no servidor
      await api.post('/auth/logout/', { refresh_token: refreshTokenValue });
    }
    
    // Limpar armazenamento local
    removeFromStorage(TOKEN_STORAGE_KEY);
    removeFromStorage(REFRESH_TOKEN_STORAGE_KEY);
    removeFromStorage(AUTH_USER_STORAGE_KEY);
    
    return true;
  } catch (error) {
    console.error('Error during logout:', error);
    
    // Mesmo com erro, limpar o armazenamento local
    removeFromStorage(TOKEN_STORAGE_KEY);
    removeFromStorage(REFRESH_TOKEN_STORAGE_KEY);
    removeFromStorage(AUTH_USER_STORAGE_KEY);
    
    throw new Error(error.response?.data?.detail || 'Error during logout');
  }
};

/**
 * Registra um novo usuário gratuito (Free Tier)
 * @param {string} email - E-mail do usuário
 * @param {string} phoneNumber - Número de WhatsApp/Telefone (opcional)
 * @returns {Promise<object>} Resultado do cadastro
 */
export const registerFreeUser = async (email, phoneNumber = null) => {
  try {
    const payload = { email };
    
    // Adicionar phone_number ao payload se fornecido
    if (phoneNumber && phoneNumber.trim()) {
      payload.phone_number = phoneNumber.trim();
    }
    
    const { data } = await api.post('/auth/register-free/', payload);
    
    return data;
  } catch (error) {
    console.error('Error registering free user:', error);
    
    // Extrair mensagem de erro específica
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Erro de validação (email já cadastrado, etc)
      if (errorData.email) {
        throw new Error(errorData.email[0] || 'E-mail inválido');
      }
      
      // Erro de validação do telefone
      if (errorData.phone_number) {
        throw new Error(errorData.phone_number[0] || 'Número de telefone inválido');
      }
      
      // Erro genérico com detail
      if (errorData.detail) {
        throw new Error(errorData.detail);
      }
    }
    
    // Erro de rate limiting (429)
    if (error.response?.status === 429) {
      throw new Error('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
    }
    
    // Erro de rede
    if (error.request && !error.response) {
      throw new Error('Erro de conexão. Verifique sua internet.');
    }
    
    throw new Error('Erro ao criar conta gratuita. Tente novamente.');
  }
};

/**
 * Altera a senha do usuário
 * @param {string} username - Nome de usuário
 * @param {string} currentPassword - Senha atual
 * @param {string} newPassword - Nova senha
 * @returns {Promise<object>} Resultado da operação
 */
export const changePassword = async (username, currentPassword, newPassword) => {
  try {
    const { data } = await api.post('/auth/change-password/', {
      username,
      current_password: currentPassword,
      new_password: newPassword
    });
    
    return data;
  } catch (error) {
    console.error('Error changing password:', error);
    
    // Handle specific error cases
    if (error.response) {
      const status = error.response.status;
      const detail = error.response.data?.detail || error.response.data?.message;
      
      switch (status) {
        case 400:
          if (detail && detail.toLowerCase().includes('current password')) {
            throw new Error('Current password is incorrect');
          }
          throw new Error(detail || 'Invalid password change request');
        case 401:
          throw new Error('Authentication required');
        case 403:
          throw new Error('Access denied');
        default:
          throw new Error(detail || 'Error changing password');
      }
    } else if (error.request) {
      throw new Error('Network connection error');
    } else {
      throw new Error('Error changing password');
    }
  }
};

/**
 * Registra um novo usuário
 * @param {string} username - Nome de usuário
 * @param {string} password - Senha
 * @param {string} email - Email do usuário (opcional)
 * @param {string} fullName - Nome completo do usuário (opcional)
 * @returns {Promise<object>} Dados do usuário registrado
 */
export const registerUser = async (username, password, email = null, fullName = null) => {
  try {
    const { data } = await api.post('/auth/register/', {
      username,
      password,
      email,
      full_name: fullName
    });
    
    return data;
  } catch (error) {
    console.error('Error registering user:', error);
    throw new Error(error.response?.data?.detail || 'Error registering user');
  }
};

/**
 * Obtém os dados do usuário armazenados localmente
 * @returns {object|null} Dados do usuário ou null se não estiver autenticado
 */
export const getStoredUser = () => {
  return getFromStorage(AUTH_USER_STORAGE_KEY);
};

/**
 * Verifica se o usuário atual tem permissão de administrador
 * @returns {boolean} Se o usuário é administrador
 */
export const isAdmin = () => {
  const user = getFromStorage(AUTH_USER_STORAGE_KEY);
  return user?.is_admin === true;
};

/**
 * Verifica se o usuário existe no sistema
 * @param {string} username - Nome de usuário a verificar
 * @returns {Promise<boolean>} Se o usuário existe
 */
export const checkUserExists = async (username) => {
  try {
    const { data } = await api.get(`/auth/user-exists/${username}/`);
    return data.exists;
  } catch (error) {
    console.error('Error checking if user exists:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao verificar se o usuário existe'));
  }
};

/**
 * Lista todos os usuários (apenas para administradores)
 * @returns {Promise<array>} Lista de usuários
 */
export const listUsers = async () => {
  try {
    const { data } = await api.get('/auth/users/');
    return data.users;
  } catch (error) {
    console.error('Error listing users:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao listar usuários'));
  }
};

/**
 * Solicita reset de senha enviando nova senha para o email cadastrado
 * @param {string} username - Nome de usuário
 * @returns {Promise<object>} Resultado da operação
 */
export const forgotPassword = async (username) => {
  try {
    const { data } = await api.post('/auth/forgot-password/', {
      username: username.trim()
    });
    
    return data;
  } catch (error) {
    console.error('Error requesting password reset:', error);
    
    // Handle specific error cases
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.response.data?.detail;
      
      switch (status) {
        case 400:
          if (message && message.includes(translateError('Nome de usuário é obrigatório'))) {
            throw new Error(translateError('Nome de usuário é obrigatório'));
          }
          if (message && message.includes('não possui email cadastrado')) {
            throw new Error('Usuário não possui email cadastrado');
          }
          throw new Error(message || translateError('Dados inválidos'));
        case 404:
          throw new Error('Usuário não encontrado');
        case 500:
          throw new Error('Erro interno do servidor. Tente novamente.');
        default:
          throw new Error(message || translateError('Erro ao processar solicitação'));
      }
    } else if (error.request) {
      throw new Error(translateError('Erro de conexão. Verifique sua internet e tente novamente.'));
    } else {
      throw new Error(translateError('Erro ao processar solicitação'));
    }
  }
}; 